<template>
  <div id="app" class="box">

    <!-- Header -->
    <div class="header">
      <div class="header-left">
        <i :class="'qi-' + weather.icon + '-fill'"></i>
        <div>
          <div class="header-title">光伏实时数据监控</div>
          <div class="weather-bar">
            <div class="weather-text">
              {{ weather.text }} · {{ weather.temp }}℃ · 体感 {{ weather.feelsLike }}℃
            </div>
          </div>
        </div>
      </div>
      <div class="status-badge" :class="connected ? 'online' : 'offline'">
        <span class="status-dot"></span>
        <span>{{ connected ? '在线' : '离线' }}</span>
      </div>
    </div>

    <!-- 总览卡片 -->
    <div class="overview-card">
      <div class="overview-inner">
        <div class="overview-label">总发电功率</div>
        <div class="overview-total">
          {{ totalW }}<span class="unit-lg">W</span>
        </div>
        <div class="overview-split">
          <div class="overview-mini">
            <span class="mini-dot pv1"></span>
            <span class="mini-label">光伏1</span>
            <span class="mini-val">{{ pv1.w }}</span>
            <span class="mini-unit">W</span>
          </div>
          <div class="overview-mini">
            <span class="mini-dot pv2"></span>
            <span class="mini-label">光伏2</span>
            <span class="mini-val">{{ pv2.w }}</span>
            <span class="mini-unit">W</span>
          </div>
        </div>
        <!-- 太阳能统计 -->
        <div class="overview-stat-section">
          <span class="overview-stat-title">☀️ 太阳能累计发电</span>
          <div class="overview-stat-row">
            <div class="stat-inline">
              <span class="stat-inline-label">日</span>
              <span class="stat-inline-value">{{ statDay }}</span>
              <span class="stat-inline-unit">kWh</span>
            </div>
            <div class="stat-inline">
              <span class="stat-inline-label">周</span>
              <span class="stat-inline-value">{{ statWeek }}</span>
              <span class="stat-inline-unit">kWh</span>
            </div>
            <div class="stat-inline">
              <span class="stat-inline-label">月</span>
              <span class="stat-inline-value">{{ statMonth }}</span>
              <span class="stat-inline-unit">kWh</span>
            </div>
            <div class="stat-inline">
              <span class="stat-inline-label">年</span>
              <span class="stat-inline-value">{{ statYear }}</span>
              <span class="stat-inline-unit">kWh</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PV 卡片 -->
    <div class="pv-wrap">
      <!-- PV1 -->
      <div class="card">
        <div class="card-title">
          <span class="title-dot"></span>光伏板 1-100w
        </div>
        <div class="data-grid">
          <div class="data-item">
            <span class="data-label">电压</span>
            <span class="data-value">{{ pv1.v }} <span class="data-unit">V</span></span>
          </div>
          <div class="data-item">
            <span class="data-label">电流</span>
            <span class="data-value">{{ pv1.a }} <span class="data-unit">A</span></span>
          </div>
          <div class="data-item highlight">
            <span class="data-label">功率</span>
            <span class="data-value">{{ pv1.w }} <span class="data-unit">W</span></span>
          </div>
          <div class="data-item">
            <span class="data-label">分流</span>
            <span class="data-value">{{ pv1.shunt }} <span class="data-unit">mV</span></span>
          </div>
        </div>
      </div>

      <!-- PV2 -->
      <div class="card">
        <div class="card-title">
          <span class="title-dot"></span>光伏板 2-200w
        </div>
        <div class="data-grid">
          <div class="data-item">
            <span class="data-label">电压</span>
            <span class="data-value">{{ pv2.v }} <span class="data-unit">V</span></span>
          </div>
          <div class="data-item">
            <span class="data-label">电流</span>
            <span class="data-value">{{ pv2.a }} <span class="data-unit">A</span></span>
          </div>
          <div class="data-item highlight">
            <span class="data-label">功率</span>
            <span class="data-value">{{ pv2.w }} <span class="data-unit">W</span></span>
          </div>
          <div class="data-item">
            <span class="data-label">分流</span>
            <span class="data-value">{{ pv2.shunt }} <span class="data-unit">mV</span></span>
          </div>
        </div>
      </div>
    </div>


    <!-- 电池卡片 -->
    <div class="card battery-card">
      <div class="card-title">
        <div class="mini-battery" :class="{ charging: charging, full: dx.soc >= 100 }">
          <div class="mini-battery-cap"></div>
          <div class="mini-battery-body">
            <div class="mini-battery-fill" :style="{ width: dx.soc + '%' }"></div>

            <!-- 电量数字在电池内部 -->
            <span class="mini-battery-text" :class="{ 'low': dx.soc <= 25 }">{{ dx.soc }}%</span>
          </div>
        </div>
        电池状态 · {{ dx.deviceName }}
      </div>
      <div class="battery-grid">

        <div class="data-item">
          <span class="data-label">电池温度</span>
          <span class="data-value">{{ dx.temp }}<span class="data-unit">°C</span></span>
        </div>

        <div class="data-item highlight">
          <span class="data-label">充电功率</span>
          <span class="data-value">{{ dx.ip }}<span class="data-unit">W</span></span>
          <span class="data-sub">充满需 {{ dx.it !== null ? dx.it : '--' }}h</span>
        </div>
        <div class="data-item highlight">
          <span class="data-label">输出功率</span>
          <span class="data-value">{{ dx.op }}<span class="data-unit">W</span></span>
          <span class="data-sub">可输出 {{ dx.ot !== null ? dx.ot : '--' }}h</span>
        </div>


        <div class="data-item">
          <span class="data-label">信号强度</span>
          <span class="data-value">{{ dx.wsig }}<span class="data-unit">dBm</span></span>
        </div>
        <!-- 第四行：AC开关、DC开关、充电状态、在线时长 -->
        <div class="data-item">
          <span class="data-label">AC输出</span>
          <span class="data-value switch-status" :class="{ active: dx.ac }">
            {{ dx.ac ? '● 开启' : '○ 关闭' }}
          </span>
        </div>
        <div class="data-item">
          <span class="data-label">DC输出</span>
          <span class="data-value switch-status" :class="{ active: dx.dc }">
            {{ dx.dc ? '● 开启' : '○ 关闭' }}
          </span>
        </div>

      </div>
    </div>
    <!-- Toast -->
    <div class="toast" v-if="toastShow">{{ toastMsg }}</div>
    <!-- 底部 -->
    <div class="footer-note">数据实时更新 · Js · {{ dx.updateTime }}</div>

  </div>
</template>

<script>
import mqtt from 'mqtt'
export default {
  name: "App",
  data() {
    return {
      toastShow: false,
      toastMsg: '',
      statDay: '0.0',
      statWeek: '0.0',
      statMonth: '0.0',
      statYear: '0.0',
      connected: false,
      pv1: { v: '0.00', a: '0.00', w: '0.00', shunt: '0.00' },
      pv2: { v: '0.00', a: '0.00', w: '0.00', shunt: '0.00' },
      weather: {
        text: "",
        temp: 0,
        humidity: 0,
        windSpeed: 0,
        windDir: "",
        windScale: 0,
        icon: ""
      },
      // 电池数据
      dx: {
        token: process.env.VUE_APP_DX_TOKEN,
        soc: 0,
        temp: '0.0',
        ip: 0,
        op: 0,
        ac: false,
        dc: false,
        acv: '0.0',
        hz: 0,
        ot: '0.0',
        pv: 0,
        it: null,
        wsig: 0,
        ec: 0,
        updateTime: ''
      },
      charging: false
    };
  },
  computed: {
    batteryFull() {
      return this.dx.soc >= 100
    },
    totalW() {
      const w1 = parseFloat(this.pv1.w) || 0;
      const w2 = parseFloat(this.pv2.w) || 0;
      return (w1 + w2).toFixed(2);
    }
  },

  mounted() {
    this.loadWeather()
    this.initMqtt();
    this.loadDxData();
    this.loadStatData()
    this.startDxTimer();

    // 监听页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibility)
  },
  beforeDestroy() {
    this.stopDxTimer()
    document.removeEventListener('visibilitychange', this.handleVisibility)
  },

  methods: {
    showToast(msg, duration = 2000) {
      this.toastMsg = msg
      this.toastShow = true
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toastShow = false
      }, duration)
    },
    async loadWeather() {
      const key = process.env.VUE_APP_WEATHER_KEY
      const location = process.env.VUE_APP_WEATHER_LOCATION

      const url = `https://nd7aarvhq8.re.qweatherapi.com/v7/weather/now?location=${location}&key=${key}`

      try {
        const res = await fetch(url)
        const j = await res.json()
        console.log(j);


        if (j.code !== "200") return

        const now = j.now
        const t = now.text
        console.log(t)
        this.weather = {
          temp: now.temp,
          feelsLike: now.feelsLike,
          text: now.text,
          windDir: now.windDir,
          windScale: now.windScale,
          humidity: now.humidity,
          icon: now.icon
        }
        this.showToast('数据已刷新')

      } catch (e) {
        console.log("天气请求失败", e)
      }
    },
    // 获取统计数据
    async fetchStat(dateType) {
      const now = new Date()
      let beginDate, endDate

      switch (dateType) {
        case 'day':
          beginDate = this.formatDate(now)
          endDate = beginDate
          break
        case 'week': {
          const dow = now.getDay() || 7
          const mon = new Date(now)
          mon.setDate(now.getDate() - dow + 1)
          const sun = new Date(mon)
          sun.setDate(mon.getDate() + 6)
          beginDate = this.formatDate(mon)
          endDate = this.formatDate(sun)
          break
        }
        case 'month':
          beginDate = this.formatDate(new Date(now.getFullYear(), now.getMonth(), 1))
          endDate = this.formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0))
          break
        case 'year':
          beginDate = `${now.getFullYear()}-01-01`
          endDate = `${now.getFullYear()}-12-31`
          break
      }

      try {
        const res = await fetch(
          `https://iot.dx2.cn/v1/device/stat?beginDate=${beginDate}&dateType=${dateType}&endDate=${endDate}&deviceSn=855226042300287&key=scs`,
          {
            headers: {
              token: this.dx.token
            }
          })
        const j = await res.json()
        if (j.code === 0) return j.data.total || 0
        return 0
      } catch {
        return 0
      }
    },

    // 加载所有统计
    async loadStatData() {
      const [day, week, month, year] = await Promise.all([
        this.fetchStat('day'),
        this.fetchStat('week'),
        this.fetchStat('month'),
        this.fetchStat('year')
      ])
      this.statDay = day.toFixed(1)
      this.statWeek = week.toFixed(1)
      this.statMonth = month.toFixed(1)
      this.statYear = year.toFixed(1)
    },

    // 格式化日期
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    // 启动定时器
    startDxTimer() {
      this.stopDxTimer()  // 先清除旧的
      this.dxTimer = setInterval(() => {
        this.loadDxData()
      }, 5000)
    },

    // 停止定时器
    stopDxTimer() {
      if (this.dxTimer) {
        clearInterval(this.dxTimer)
        this.dxTimer = null
      }
    },

    // 页面可见性变化处理
    handleVisibility() {
      if (document.hidden) {
        // 页面不可见 → 停止刷新
        this.stopDxTimer()
        console.log('页面隐藏，停止数据刷新')
      } else {
        // 页面恢复可见 → 立即刷新一次 + 重启定时器
        this.loadDxData()
        this.startDxTimer()
        console.log('页面显示，恢复数据刷新')
      }
    },

    async loadDxData() {
      try {
        const res = await fetch('https://iot.dx2.cn/v1/device/property?deviceId=600406573390495744', {
          headers: {
            token: this.dx.token
          }
        })
        const j = await res.json()
        if (j.code !== 0) {
          return this.showToast('数据加载失败:' + JSON.stringify(j))
        }
        const p = j.data.properties
        const dev = j.data.device

        // 电池核心
        this.dx.soc = p.rb ?? 0
        this.dx.temp = ((p.bt || 0) / 10).toFixed(1)

        // 输入功率
        this.dx.ip = p.ip ?? 0          // 总输入
        this.dx.pv = p.pal ?? 0         // 光伏输入
        this.dx.acip = p.acip ?? 0      // 市电输入
        this.dx.cip = p.cip ?? 0        // 车充输入

        // 输出功率
        this.dx.op = p.op ?? 0          // 总输出
        this.dx.ac = p.oac === 1        // AC输出开关
        this.dx.dc = p.odc === 1        // DC输出开关
        this.dx.acv = ((p.acov || 0) / 10).toFixed(1)   // AC电压
        this.dx.hz = (p.acohz || 0) / 10                 // AC频率

        // 时间相关
        this.dx.ot = ((p.ot || 0) / 10).toFixed(1)      // 剩余放电时间
        this.dx.it = p.it != null ? (p.it / 10).toFixed(1) : null  // 剩余充满时间

        // 网络与系统
        this.dx.wsig = p.wsig ?? 0      // WiFi信号
        this.dx.uptime = p.uo ? (p.uo / 3600).toFixed(1) : '0.0'  // 在线时长

        // 设备信息
        this.dx.deviceName = dev?.deviceName || 'JE-1500D'

        // 更新时间
        this.dx.updateTime = new Date().toLocaleString('zh-CN')

        // 充电状态判断：总输入功率 > 1W 即为充电中
        this.charging = this.dx.ip > 1



      } catch (e) {
        console.error('电池数据加载失败:', e)
      }
    },

    initMqtt() {
       const host = 'wss://sun.jxell.top:8084/mqtt'
       const client = mqtt.connect(host, {
        username: process.env.VUE_APP_MQTT_USERNAME,
        password: process.env.VUE_APP_MQTT_PASSWORD,
        reconnectPeriod: 5000,
        clientId: `web_${Date.now()}`
      });

      client.on('connect', () => {
        this.connected = true;
        client.subscribe('pv/real');
      });

      client.on('close', () => {
        this.connected = false;
      });
      client.on('error', () => {
        this.connected = false;
      });
      client.on('offline', () => {
        this.connected = false;
      });

      client.on('message', (topic, payload) => {
        if (topic !== 'pv/real') return;
        try {
          const d = JSON.parse(payload.toString());

          this.pv1.v = d.pv1.v;
          this.pv1.w = d.pv1.w;
          this.pv1.a = (d.pv1.a < 0 ? 0 : d.pv1.a).toFixed(2);
          this.pv1.shunt = (d.pv1.shunt < 0 ? 0 : d.pv1.shunt).toFixed(2);


          this.pv2.v = d.pv2.v;
          this.pv2.w = d.pv2.w;
          this.pv2.a = (d.pv2.a < 0 ? 0 : d.pv2.a).toFixed(2);
          this.pv2.shunt = (d.pv2.shunt < 0 ? 0 : d.pv2.shunt).toFixed(2);

        } catch (e) {
          // 数据解析失败，静默处理
        }
      });
    }
  }

};
</script>
